import { getModelLoaderType } from '@aibuild/model-viewer';

export type AttachmentKind = 'image' | 'pdf' | 'dxf' | 'model' | 'other';

/** Image formats every supported browser can decode from a blob URL. */
const IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/svg+xml',
]);

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp', 'svg']);

const DXF_MIME_TYPES = new Set([
  'image/vnd.dxf',
  'image/x-dxf',
  'application/dxf',
  'application/x-dxf',
  'drawing/x-dxf',
]);

export const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
};

/**
 * Classify an attachment for preview purposes.
 *
 * The extension is consulted as well as the MIME type: browsers report DXF as
 * anything from `image/vnd.dxf` to `application/octet-stream` to an empty
 * string depending on the OS, and drag-and-drop often supplies no type at all.
 * Mesh formats are recognised by extension alone, which is what decides the
 * loader anyway — no browser agrees on a MIME type for them.
 */
export const classifyAttachment = (contentType: string, filename: string): AttachmentKind => {
  const mime = (contentType || '').toLowerCase().split(';')[0].trim();
  const ext = extensionOf(filename);

  if (ext === 'dxf' || DXF_MIME_TYPES.has(mime)) return 'dxf';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (IMAGE_MIME_TYPES.has(mime) || IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (getModelLoaderType(filename)) return 'model';
  return 'other';
};
